import { useEffect, useState } from 'react'

import {
  IconBars,
  IconButton,
  IconEllipsisH,
  IconSearch,
  IconUserCircle,
} from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router'

import { SearchModalMobile } from '../SearchModal/mobile'
import { ProfileMobile } from '~/portal/components/ProfileMobile'
import { usePageLayout } from '~/portal/hooks/usePageLayout'
import { CONSUMER_PATHNAMES } from '~/portal/routes/sp-portal/constants'
import { LandingProduct } from '~/portal/types/product'
import { UserInfo } from '~/shared/types/user.ts'

const TopBarSearch = ({ disabled, setSearchModalOpen }) => {
  const { t } = useTranslation()

  const onClick = () => {
    setSearchModalOpen(true)
  }

  return (
    <IconButton
      aria-label={t('consumer.topBar.search.product.ariaLabel')}
      icon={<IconSearch />}
      outline={true}
      size="m"
      className="sp-search-button mx-[10px] text-(--sp-color-text-muted) dark:text-(--sp-color-text-muted) border-(--sp-color-border) dark:border-(--sp-color-border) hover:bg-(--sp-color-surface-hover) dark:hover:bg-(--sp-color-surface-hover) active:bg-(--sp-color-surface-hover) dark:active:bg-(--sp-color-surface-hover)"
      data-testid="topbar-search-mobile"
      disabled={disabled}
      onClick={onClick}
    />
  )
}

const TopBarProfile = ({ disabled, user, setIsProfileOpen }) => {
  const { t } = useTranslation()

  return (
    <div className="mr-[10px]">
      {user?.email ? (
        <IconButton
          data-testid="topbar-profile-mobile"
          aria-label={t('consumer.topBar.profile.ariaLabel')}
          icon={<IconUserCircle />}
          outline={true}
          size="m"
          disabled={disabled}
          onClick={() => setIsProfileOpen(true)}
          className="sp-topbar-profile-button text-(--sp-color-text-muted) dark:text-(--sp-color-text-muted) border-(--sp-color-border) dark:border-(--sp-color-border) hover:bg-(--sp-color-surface-hover) dark:hover:bg-(--sp-color-surface-hover) active:bg-(--sp-color-surface-hover) dark:active:bg-(--sp-color-surface-hover)"
        />
      ) : (
        <IconButton
          data-testid="topbar-login-mobile"
          aria-label={t('consumer.topBar.logIn.ariaLabel')}
          icon={<IconEllipsisH />}
          outline={true}
          size="m"
          disabled={disabled}
          onClick={() => setIsProfileOpen(true)}
          className="sp-topbar-profile-button text-(--sp-color-text-muted) dark:text-(--sp-color-text-muted) border-(--sp-color-border) dark:border-(--sp-color-border) hover:bg-(--sp-color-surface-hover) dark:hover:bg-(--sp-color-surface-hover) active:bg-(--sp-color-surface-hover) dark:active:bg-(--sp-color-surface-hover)"
        />
      )}
    </div>
  )
}

export interface TopBarMobileProps {
  accentColor?: string,
  fontFamily?: string,
  logo?: string,
  logoHeight?: number,
  name?: string,
  user?: UserInfo,
  baseUrl?: string,
  currentProduct?: LandingProduct,
  accentDivider?: boolean,
  disabled?: boolean,
  hideStripes?: boolean,
  toggleSideNavWithTransition?: (value: boolean) => void,
  products?: Array<LandingProduct>,
  showNavToggle?: boolean,
}

export const TopBarMobile = ({
  accentColor,
  fontFamily,
  logo,
  logoHeight,
  name,
  user,
  baseUrl,
  currentProduct,
  accentDivider = false,
  disabled = false,
  hideStripes = false,
  products,
  toggleSideNavWithTransition,
  showNavToggle = false,
}: TopBarMobileProps) => {

  const { t } = useTranslation()

  const location = useLocation()
  
  const { consumerTopBar } = usePageLayout()

  const [isProfileOpen, setIsProfileOpen] = useState(false)

  const [searchModalOpen, setSearchModalOpen] = useState(false)
  
  useEffect(() => {
    if (searchModalOpen) {
      setSearchModalOpen(false)
    }
  }, [location.pathname])
  
  const isStatusPage = CONSUMER_PATHNAMES.includes(location.pathname)

  return (
    <div>
      <nav
        className={clsx(
          'sp-topbar w-full flex items-center justify-between h-[3.75rem] mx-auto bg-(--sp-color-surface) z-50 relative',
          !isStatusPage && 'border-(--sp-color-border-subtle) border-b border-solid'
        )}
        aria-label={t('consumer.topBar.ariaLabel')}
        data-testid="topbar-mobile"
      >
        {disabled && !hideStripes && <div data-testid="bg-stripes" className="absolute w-full h-full bg-stripes bg-(--sp-color-surface-sunken)" />}
        {(currentProduct || showNavToggle) && (
          <IconButton
            aria-label={t('consumer.edit.buttons.topBar.sideMenu.openButton.ariaLabel')}
            icon={<IconBars />}
            onClick={() => toggleSideNavWithTransition?.(true)}
            outline={true}
            size="m"
            className="sp-topbar-hamburger mx-[10px] z-[100] text-(--sp-color-text-muted) dark:text-(--sp-color-text-muted) border-(--sp-color-border) dark:border-(--sp-color-border) hover:bg-(--sp-color-surface-hover) dark:hover:bg-(--sp-color-surface-hover) active:bg-(--sp-color-surface-hover) dark:active:bg-(--sp-color-surface-hover)"
            data-testid="topbar-sidenav-open"
          />
        )}
        <Link
          to={baseUrl || '/'}
          className={clsx(
            'sp-topbar-logo p-0 flex h-full items-center duration-200 transition-color flex-grow-0 xl:max-w-[250px]',
            disabled && 'pointer-events-none'
          )}
          aria-label={t('consumer.topBar.landingPageLink.ariaLabel')}
        >
          {logo ? <img className={clsx('w-auto object-fill max-w-full z-[1]', logoHeight ? undefined : 'h-full', disabled && 'opacity-80')} style={logoHeight ? { height: `${logoHeight}px` } : undefined} src={logo} alt="logo" /> : <div className="font-semibold text-h4 z-[1] h-full line-clamp-2 break-all content-center">{name}</div>}
        </Link>

        <div className="flex-1" />

        {!consumerTopBar?.search.disabled && (
          <TopBarSearch
            disabled={disabled}
            setSearchModalOpen={setSearchModalOpen}
          />
        )}
        
        <div className="sp-topbar-actions gap-x-6 flex items-center justify-end flex-grow-0 flex-shrink-0 h-[3.75rem] z-[99]">
          <TopBarProfile
            disabled={disabled}
            user={user}
            setIsProfileOpen={setIsProfileOpen}
          />
        </div>
      </nav>

      {accentDivider && <div
        data-testid="divider"
        className="sp-accent-divider h-1.5 bg-(--sp-accent-divider-color,var(--sp-color-accent))"
      />}
      {isProfileOpen && (
        <ProfileMobile
          title={user?.email ? t('components.profile.mobileLabel.profile') : t('components.profile.mobileLabel.menu')}
          user={user}
          isOpen={isProfileOpen}
          close={() => setIsProfileOpen(false)}
          accentColor={accentColor}
          fontFamily={fontFamily}
        />
      )}
      {searchModalOpen && <SearchModalMobile
        isOpen={searchModalOpen}
        onClose={() => setSearchModalOpen(false)} 
        accentColor={accentColor}
        products={products}
        currentProduct={currentProduct}
      />}
    </div >
  )
}